import React, { useEffect, useState, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import moment from "moment";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import {
  Session,
  Turn,
  EvaluateResult,
} from "../../model_gen/session";
import { ChatCompletionRequestMetadata } from "../../model_gen/chat_metadata";
import { TestSet } from "../../model_gen/evaluation";
import TestSetContextMenu from "../TestSet/TestSetContextMenu";
import { RequestsService } from "../../services/RequestsService";
import { TestSetService } from "../../services/TestSetService";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Collapsible, 
  CollapsibleTrigger,  
  CollapsibleContent 
} from "../ui/collapsible";


import { Tooltip } from "antd";
import { MultiSelectOption } from "../ui/multi-select";
import { 
  CopyIcon,
  InfoIcon,
  PlusIcon,
  HistoryIcon,
  ServerIcon,
  ClockIcon,
  ExternalLinkIcon,
  BarChart3Icon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { message } from "antd";
import AgentContext, { SessionContext } from "./AgentContext";
import AgentTimelineCard from "./AgentTimelineCard";
import { isInternalRequestType } from "../../types/AgentInternalRequestType";

interface AgentSessionOverviewProps {
  open: boolean;
  onClose: () => void;
  agentId: number;
  sessionId: string;
  applicationId?: number;
  executionMode?: string;
}


const formatSessionId = (sessionId: string) => {
  if (!sessionId) return "";
  return `...${sessionId.slice(-8)}`;
};

// Theme-aware text formatter to replace formatTextOrJson
const formatResponseContent = (text?: string): React.ReactNode => {
  if (!text) {
    return null;
  }

  // Check if the text contains [ or {
  if (text.includes("[") || text.includes("{")) {
    try {
      // Attempt to parse the text as JSON
      const parsedJson = JSON.parse(text);
      const formattedText = JSON.stringify(parsedJson, null, 2);
      return (
        <pre className="bg-muted/50 border border-border rounded-lg p-3 overflow-x-auto max-w-full">
          <code className="text-foreground text-sm whitespace-pre-wrap break-words">{formattedText}</code>
        </pre>
      );
    } catch (e) {
      // If parsing fails, return the original text
      return text;
    }
  } else {
    // If the text doesn't contain [ or {, return it as is
    return text;
  }
};

const AgentSessionOverview: React.FC<AgentSessionOverviewProps> = ({
  open,
  onClose,
  agentId,
  sessionId,
  applicationId,
  executionMode,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [session, setSession] = useState<Session | null>(null);
  const [contexts, setContexts] = useState<SessionContext[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingContexts, setLoadingContexts] = useState(false);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [isLoadingOlder, setIsLoadingOlder] = useState<boolean>(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [testSetsByAction, setTestSetsByAction] = useState<
    Record<string, TestSet[]>
  >({});
  const [summaryLoading, setSummaryLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [requestMetadata, setRequestMetadata] = useState<{
    [key: string]: ChatCompletionRequestMetadata;
  }>({});
  const [openValues, setOpenValues] = React.useState<string[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | undefined>(undefined);
  const isManualSelectionRef = useRef<boolean>(false);
  const manualSelectionTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [unselectedIntents, setUnselectedIntents] = useState<Set<string>>(
    new Set()
  );
  const [availableIntents, setAvailableIntents] = useState<string[]>([]);
  const [expandedTimelines, setExpandedTimelines] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && sessionId) {
      loadAgentSessionData();
      openWebsocket();
    }
    
    return () => {
      closeWsConnection();
    };
  }, [open, sessionId, agentId, apiKey]);

  useEffect(() => {
    if (applicationId) {
      loadTestSets();
    }
  }, [applicationId, apiKey]);


  const loadAgentSessionData = (showLoading: boolean = true) => {
    if (showLoading) {
      setIsLoading(true);
      setLoadingRequests(true);
    }
    
    // Use the new timeline endpoint that fetches from PostgreSQL
    AgentService.getAgentSessionTimeline(
      agentId,
      sessionId,
      () => {},
      apiKey
    ).subscribe(
      (data: any) => {
        // Build session from timeline data
        if (data && data.timeline) {
          // Update available intents with the timeline
          updateAvailableIntents(data.timeline);
          
          // Set the session with the timeline
          setSession({
            session_id: sessionId,
            timeline: data.timeline,
            has_more: data.has_more || false,
            date_created: data.timeline[0]?.request?.date_created || 0,
            company_id: data.timeline[0]?.request?.company_id || null,
            application_id: applicationId || data.timeline[0]?.request?.application_id || null,
            meta: {}
          } as Session);
          
          // Extract request IDs from the timeline
          const requestIds = data.timeline
            .map((turn: Turn) => turn.request?.request_id)
            .filter((id: string | undefined): id is string => !!id);
          
          // If we have request IDs, fetch contexts
          if (requestIds.length > 0) {
            setLoadingContexts(true);
            // Don't clear contexts immediately to avoid flash of "no contexts"
            AgentService.getRequestContextsByRequestIds(
              agentId,
              requestIds,
              () => {},
              apiKey
            ).subscribe(
              (contextData: any) => {
                /* removed console.log of contexts data to avoid leaking sensitive info */
                setContexts(contextData?.contexts || []);
                setLoadingContexts(false);
              },
              (error: any) => {
                console.error("Error fetching contexts:", error);
                setContexts([]);
                setLoadingContexts(false);
              }
            );
          } else {
            setContexts([]);
            setLoadingContexts(false);
          }
        } else {
          // Set a default empty session if no data is found
          setSession({
            session_id: sessionId,
            timeline: [],
            date_created: 0,
            company_id: null,
            application_id: applicationId || null,
            meta: {}
          } as Session);
          setContexts([]);
          setLoadingContexts(false);
        }
        
        if (showLoading) {
          setLoadingRequests(false);
          setIsLoading(false);
        }
      },
      (error: any) => {
        console.error("Error fetching timeline data:", error);
        
        // Set empty session on error
        setSession({
          session_id: sessionId,
          timeline: [],
          date_created: 0,
          company_id: null,
          application_id: applicationId || null,
          meta: {}
        } as Session);
        setContexts([]);
        setLoadingContexts(false);
        
        if (showLoading) {
          setLoadingRequests(false);
          setIsLoading(false);
        }
      }
    );
  };

  // Agent sessions load all data at once, no pagination needed
  const loadOlderRequests = () => {};

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${encodeURIComponent(
        sessionId
      )}&type=SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      try {
        const data = JSON.parse(message.data);
        if (data.event_type === "SESSION_UPDATE") {
          handleNewSessionTurn(data);
        }
      } catch (e) {
        console.error("Failed to parse WebSocket message:", e);
      }
    });

    const checkInterval = setInterval(() => {
      const now = new Date();
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5);

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleNewSessionTurn = (data: any) => {
    // For agent sessions, when we get a SESSION_UPDATE, it means a request completed
    // Re-fetch all data without showing loading indicators
    loadAgentSessionData(false);
  };


  const removeDuplicateTurns = (timeline: Turn[]): Turn[] => {
    const uniqueTurns = new Map<string, Turn>();
    timeline.forEach((turn) => {
      if (turn.request?.request_id) {
        uniqueTurns.set(turn.request.request_id, turn);
      }
    });
    return Array.from(uniqueTurns.values());
  };

  const sortedTimeline = React.useMemo(() => {
    if (!session?.timeline) return [];
    const uniqueTimeline = removeDuplicateTurns(session.timeline);
    return uniqueTimeline.sort((a, b) => {
      const dateA = new Date(a.request?.date_created || 0);
      const dateB = new Date(b.request?.date_created || 0);
      return dateA.getTime() - dateB.getTime(); // Changed to ascending order
    });
  }, [session?.timeline]);

  // Group timeline by task ID (reference_id)
  const groupedTimeline = React.useMemo(() => {
    const groups: Map<string, Turn[]> = new Map();
    
    sortedTimeline.forEach((turn) => {
      const taskId = String(turn.request?.reference_id ?? "no-task-id");
      if (!groups.has(taskId)) {
        groups.set(taskId, []);
      }
      groups.get(taskId)!.push(turn);
    });
    
    // Convert to array and sort by first request time in each group
    return Array.from(groups.entries())
      .map(([taskId, turns]) => ({ taskId, turns }))
      .sort((a, b) => {
        const aTime = a.turns[0]?.request?.date_created || 0;
        const bTime = b.turns[0]?.request?.date_created || 0;
        return aTime - bTime;
      });
  }, [sortedTimeline]);

  // Convert available intents to options for multi-select
  const intentOptions: MultiSelectOption[] = availableIntents.map((intent) => ({
    label: intent,
    value: intent,
  }));

  // Get selected intents (those NOT in unselectedIntents)
  const selectedIntents = availableIntents.filter(
    (intent) => !unselectedIntents.has(intent)
  );

  // Handle intent selection changes from multi-select
  const handleIntentSelectionChange = (selectedValues: (string | number)[]) => {
    const selectedStrings = selectedValues.map(String);
    const newUnselected = new Set(
      availableIntents.filter((intent) => !selectedStrings.includes(intent))
    );
    setUnselectedIntents(newUnselected);
  };

  // Helper to determine if a request is an internal/admin type
  const isInternalRequest = (turn: Turn) => {
    return isInternalRequestType(turn.request?.action_type, turn.request?.request_type);
  };

  // Update the filtered timeline to exclude unselected intents and maintain grouping
  const filteredGroupedTimeline = React.useMemo(() => {
    return groupedTimeline
      .map(group => {
        // Get all turns that match the filter
        const filteredTurns = group.turns.filter(
          (turn) =>
            turn.request?.action_type &&
            !unselectedIntents.has(turn.request.action_type)
        );
        
        // Find the last non-internal action
        const nonInternalTurns = filteredTurns.filter(turn => !isInternalRequest(turn));
        const lastActionTurn = nonInternalTurns.length > 0 
          ? nonInternalTurns[nonInternalTurns.length - 1]
          : null;
        
        // Check if task is still running (has internal requests but no completed actions yet)
        const hasOnlyInternalRequests = filteredTurns.length > 0 && nonInternalTurns.length === 0;
        
        return {
          taskId: group.taskId,
          turns: filteredTurns,
          displayTurn: lastActionTurn,
          isRunning: hasOnlyInternalRequests,
          allTurns: group.turns // Keep all turns for timeline visualization
        };
      })
      .filter(group => group.turns.length > 0); // Remove empty groups
  }, [groupedTimeline, unselectedIntents]);

  // Flatten for certain operations that need a flat list
  const filteredTimeline = React.useMemo(() => {
    return filteredGroupedTimeline.flatMap(group => group.turns);
  }, [filteredGroupedTimeline]);

  // Set initial selected request when timeline is loaded
  useEffect(() => {
    if (filteredTimeline.length > 0 && !selectedRequestId) {
      const firstRequest = filteredTimeline[0]?.request?.request_id;
      if (firstRequest) {
        setSelectedRequestId(firstRequest);
      }
    }
  }, [filteredTimeline, selectedRequestId]);

  // Clean up card refs when timeline changes
  useEffect(() => {
    // Clear refs for cards that are no longer in the timeline
    const currentRequestIds = new Set(
      filteredTimeline.map(turn => turn.request?.request_id).filter(Boolean)
    );
    
    cardRefs.current.forEach((_, requestId) => {
      if (!currentRequestIds.has(requestId)) {
        cardRefs.current.delete(requestId);
      }
    });
  }, [filteredTimeline]);

  // Handle scroll-based selection
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Skip if manual selection is active
      if (isManualSelectionRef.current) return;
      
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 3; // Use top third for selection
      
      let closestCard: { id: string; distance: number } | null = null;
      
      // Find the card closest to the container's top third
      cardRefs.current.forEach((cardElement, requestId) => {
        const cardRect = cardElement.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        
        // Only consider cards that are at least partially visible
        if (cardRect.bottom > containerRect.top && cardRect.top < containerRect.bottom) {
          if (!closestCard || distance < closestCard.distance) {
            closestCard = { id: requestId, distance };
          }
        }
      });
      
      if (closestCard && closestCard.id !== selectedRequestId) {
        setSelectedRequestId(closestCard.id);
      }
    };

    // Use requestAnimationFrame for smooth updates during scrolling
    let ticking = false;
    const handleScrollRAF = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", handleScrollRAF, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      container.removeEventListener("scroll", handleScrollRAF);
    };
  }, [selectedRequestId, filteredTimeline]);

  // Auto-scroll only for manual selections (clicks)
  useEffect(() => {
    if (!selectedRequestId || !scrollContainerRef.current) return;
    
    // Only auto-scroll for manual selections
    if (!isManualSelectionRef.current) return;
    
    const selectedCard = cardRefs.current.get(selectedRequestId);
    if (!selectedCard) return;
    
    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const cardRect = selectedCard.getBoundingClientRect();
    
    // Always scroll clicked cards into view at the top third of the viewport
    const scrollTop = container.scrollTop + (cardRect.top - containerRect.top) + cardRect.height / 2 - containerRect.height / 3;
    container.scrollTo({
      top: scrollTop,
      behavior: "smooth"
    });
  }, [selectedRequestId]);


  const copyRequestId = (requestId: string) => {
    navigator.clipboard.writeText(requestId);
    message.success("Request ID copied!");
  };

  const formatDateTime = (
    date: number | undefined,
    prevDate: number | undefined,
    nextDate: number | undefined
  ) => {
    if (!date) return { display: "", full: "" };
    const currentMoment = moment(date);
    const prevMoment = prevDate ? moment(prevDate) : null;
    const nextMoment = nextDate ? moment(nextDate) : null;

    const showFullDate =
      (prevMoment && !currentMoment.isSame(prevMoment, "day")) ||
      (nextMoment && !currentMoment.isSame(nextMoment, "day"));

    const fullDateTime = currentMoment.format("MMM D, YYYY HH:mm:ss A");
    const displayDateTime = showFullDate
      ? fullDateTime
      : currentMoment.format("HH:mm:ss");

    return { display: displayDateTime, full: fullDateTime };
  };

  const handleViewRequestClick = (requestId: string) => {
    // Get the request to find its application_id
    const request = getRequestFromTimeline(requestId);
    const appId = applicationId || request?.application_id;
    
    if (appId) {
      window.open(
        `/application/${appId}/session/${encodeURIComponent(
          sessionId
        )}/request/${requestId}`,
        "_blank"
      );
    }
  };

  const handleViewTaskClick = (taskId: string) => {
    // Navigate to the task detail page with session ID
    window.open(
      `/agent/${agentId}/session/${encodeURIComponent(sessionId)}/task/${encodeURIComponent(taskId)}`,
      "_blank"
    );
  };

  const loadTestSets = () => {
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        const testSets = response || [];
        const groupedTestSets = testSets.reduce(
          (acc: Record<string, TestSet[]>, testSet: TestSet) => {
            const actionType = testSet?.intent_group?.intent_name;
            if (!acc[actionType]) {
              acc[actionType] = [];
            }
            acc[actionType].push(testSet);
            return acc;
          },
          {}
        );

        setTestSetsByAction(groupedTestSets);
      },
      (error: any) => {
        console.error("Failed to load test sets:", error);
      }
    );
  };

  const handleTestSetUpdate = (
    updatedRequestId: string,
    updatedTestSetIds: string[]
  ) => {
    setSession((prevSession) => {
      if (!prevSession) return prevSession;

      const updatedTimeline = prevSession.timeline.map((t) => {
        if (t.request?.request_id === updatedRequestId) {
          return {
            ...t,
            request: {
              ...t.request,
              metadata: {
                ...t.request.metadata,
                test_set_ids: JSON.stringify(updatedTestSetIds),
              },
            },
          };
        }
        return t;
      });

      return {
        ...prevSession,
        timeline: updatedTimeline,
      };
    });
    loadTestSets();
  };

  const fetchRequestSummary = (turn: Turn) => {
    const requestId = turn.request?.request_id;
    if (!requestId || requestMetadata[requestId]) return;

    setSummaryLoading((prev) => ({ ...prev, [requestId]: true }));

    RequestsService.getRequestMetadata(turn.request, null, apiKey).subscribe(
      (result: any) => {
        if (result.request_id) {
          setRequestMetadata((prev) => ({
            ...prev,
            [result.request_id]: result.metadata,
          }));
        }
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      },
      (error: any) => {
        console.error("Failed to fetch metadata:", error);
        setSummaryLoading((prev) => ({ ...prev, [requestId]: false }));
      }
    );
  };

  const getExistingTestSetIds = (metadata?: any) => {
    try {
      return metadata?.test_set_ids
        ? JSON.parse(metadata.test_set_ids).map(String)
        : [];
    } catch (e) {
      console.error("Error parsing test_set_ids:", e);
      return [];
    }
  };

  const updateAvailableIntents = React.useCallback((timeline: Turn[]) => {
    const newIntents = new Set<string>();
    timeline.forEach((turn) => {
      if (turn.request?.action_type) {
        newIntents.add(turn.request.action_type);
      }
    });

    setAvailableIntents((prev) => {
      const allIntents = Array.from(
        new Set([...prev, ...Array.from(newIntents)])
      );
      return allIntents.sort();
    });
  }, []);

  // Helper to get request from timeline
  const getRequestFromTimeline = (requestId: string) => {
    if (!session?.timeline) return null;
    
    const turn = session.timeline.find((t: Turn) => t.request?.request_id === requestId);
    return turn?.request || null;
  };

  // Helper functions for styling







  if (isLoading && session === null) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
            <DialogTitle className="text-lg font-gc-med">
              Session Details
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <div className="flex h-full">
              {/* Left side - Timeline skeleton */}
              <div className="flex-1 overflow-auto border-r border-border">
                <div className="p-6 space-y-6">
                  {/* Session Information Skeleton */}
                  <div className="space-y-6">
                    {/* Key Metrics Grid Skeleton */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-muted rounded animate-pulse" />
                            <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                          </div>
                          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                        </div>
                      ))}
                    </div>

                    {/* Intent Filter Skeleton */}
                    <Card className="border-border">
                      <CardContent className="p-4 space-y-3">
                        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                        <div className="h-10 w-full bg-muted rounded animate-pulse" />
                      </CardContent>
                    </Card>

                    {/* Agent Timeline Card Skeleton */}
                    <Card className="border-border">
                      <CardContent className="p-4">
                        <div className="h-6 w-40 bg-muted rounded animate-pulse mb-4" />
                        <div className="space-y-2">
                          <div className="h-4 w-full bg-muted rounded animate-pulse" />
                          <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Timeline Section Skeleton */}
                  <div className="space-y-8">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-muted rounded animate-pulse" />
                      <div className="h-6 w-24 bg-muted rounded animate-pulse" />
                    </div>
                    
                    {/* Timeline Items Skeleton */}
                    <div className="space-y-6">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="relative pl-8">
                          {/* Timeline dot */}
                          <div className="absolute left-0 top-6 w-3 h-3 bg-muted rounded-full animate-pulse" />
                          {i < 3 && (
                            <div className="absolute top-6 left-1.5 w-px h-20 bg-muted animate-pulse" />
                          )}
                          
                          <Card className="border-border">
                            <CardContent className="p-6">
                              <div className="space-y-4">
                                {/* Header skeleton */}
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-2 flex-1">
                                    <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                                    <div className="h-5 w-24 bg-muted rounded animate-pulse" />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <div className="h-7 w-16 bg-muted rounded animate-pulse" />
                                    <div className="h-7 w-7 bg-muted rounded animate-pulse" />
                                  </div>
                                </div>
                                
                                {/* Content skeleton */}
                                <div className="space-y-2">
                                  <div className="h-4 w-full bg-muted rounded animate-pulse" />
                                  <div className="h-4 w-4/5 bg-muted rounded animate-pulse" />
                                  <div className="h-4 w-3/5 bg-muted rounded animate-pulse" />
                                </div>
                                
                                {/* Metadata skeleton */}
                                <div className="flex items-center gap-4">
                                  <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                                  <div className="h-3 w-28 bg-muted rounded animate-pulse" />
                                  <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right side - Context skeleton */}
              <div className="w-[400px] overflow-auto bg-muted/20">
                <div className="p-4">
                  <div className="h-5 w-32 bg-muted rounded animate-pulse mb-4" />
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <Card key={i} className="border-border">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                            <div className="h-4 w-4 bg-muted rounded animate-pulse" />
                          </div>
                          <div className="space-y-2">
                            <div className="h-3 w-full bg-muted rounded animate-pulse" />
                            <div className="h-3 w-4/5 bg-muted rounded animate-pulse" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const getFaultSummary = (turn: Turn) => {
    let faultSummary = turn.eval_request?.eval_results_set?.evaluate_summary;
    let suggestedCorrection = "";
    turn?.eval_request?.eval_results_set?.evaluation_results?.forEach(
      (val: EvaluateResult) => {
        if (!faultSummary && val.status == "FAULT") {
          faultSummary = val.description;
        }
        if (!suggestedCorrection && val.status == "FAULT") {
          suggestedCorrection = val.meta.correction as string;
        }
      }
    );
    
    return (
      <div className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-gc-med text-foreground">Reason</p>
          <p className="text-sm text-foreground">{faultSummary}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-gc-med text-foreground">
            {turn.response?.correction_applied ? "Original" : "Suggested Correction"}
          </p>
          <div className="text-sm text-foreground">
            {formatResponseContent(
              turn.response?.correction_applied
                ? turn.eval_request?.text_content
                : suggestedCorrection
            )}
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="flex h-full">
      {/* Left side - Timeline */}
      <div className="flex-1 overflow-auto border-r border-border relative" ref={scrollContainerRef}>
        <div className="p-6 space-y-6">
          {/* Session Information Section */}
          <div className="space-y-6">
            {session ? (
              <>
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <HistoryIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Session ID</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
                        {formatSessionId(session?.session_id)}
                      </code>
                      <Tooltip title="Copy Session ID">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => {
                            navigator.clipboard.writeText(session?.session_id || "");
                          }}
                        >
                          <CopyIcon className="w-3 h-3" />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Date Started</p>
                    </div>
                    <p className="text-sm font-gc-reg text-foreground">
                      {(() => {
                        const firstTurn = sortedTimeline[0];
                        const dateCreated = firstTurn?.request?.date_created || session?.date_created;
                        return dateCreated ? moment(dateCreated).format("MM/DD/YYYY hh:mm:ss A") : "-";
                      })()}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Date Ended</p>
                    </div>
                    <p className="text-sm font-gc-reg text-foreground">
                      {(() => {
                        const lastTurn = sortedTimeline[sortedTimeline.length - 1];
                        const dateEnded = lastTurn?.response?.date_created || lastTurn?.request?.date_created;
                        return dateEnded ? moment(dateEnded).format("MM/DD/YYYY hh:mm:ss A") : "-";
                      })()}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ServerIcon className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground font-gc-reg">Mode</p>
                    </div>
                    <p className="text-sm font-gc-reg text-foreground">
                      {executionMode || "direct"}
                    </p>
                  </div>
                </div>


              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Session still processing; please try again later
              </p>
            )}
          </div>

          {/* Timeline Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-maitai-green" />
              <h2 className="text-lg font-gc-med text-foreground">Timeline</h2>
            </div>

            {/* Timeline Items */}
            <div className="space-y-8">
              {filteredGroupedTimeline.map((group, groupIndex) => {
                // Use displayTurn if available, otherwise show running state
                const turn = group.displayTurn || (group.isRunning ? group.turns[group.turns.length - 1] : null);
                if (!turn) return null;
                
                return (
                  <div key={group.taskId} className="relative">
                    {/* Individual Turns within Task */}
                    <div className="space-y-6">
                      {(() => {
                          const actualTurnIndex = group.turns.length - 1;
                          const prevTurn = actualTurnIndex > 0 ? group.turns[actualTurnIndex - 1] : null;
                          const nextTurn: Turn | null = null; // Last turn has no next
                          const { display: dateTimeString, full: fullDateTimeString } =
                            formatDateTime(
                              turn.request?.date_created,
                              prevTurn?.request?.date_created,
                              nextTurn?.request?.date_created
                            );
                
                let faultySentinel = "";
                let faultCount = 0;
                turn.eval_request?.eval_results_set?.evaluation_results?.forEach(
                  (evalResult: EvaluateResult) => {
                    if (evalResult.status == "FAULT" && faultySentinel == "") {
                      faultySentinel = evalResult.sentinel_name || "";
                    }
                    if (evalResult.status == "FAULT") {
                      faultCount += 1;
                    }
                  }
                );
                
                
                let requestMetadataLocal: any = {};
                try {
                  requestMetadataLocal = JSON.parse(
                    (turn.request?.metadata?._maitai_request_meta as string) || "{}"
                  );
                } catch (e) {
                  console.error("Failed to parse _maitai_request_meta:", e);
                  requestMetadataLocal = {};
                }
                
                // Use fetched metadata if available
                const metadata = turn.request?.request_id && requestMetadata[turn.request.request_id] 
                  ? requestMetadata[turn.request.request_id] 
                  : requestMetadataLocal;

                return (
                  <div
                    key={turn.request?.request_id}
                    data-request-id={turn.request?.request_id}
                    className={cn(
                      "relative pl-8"
                    )}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-1.5 top-12">
                      <div className={cn(
                        "rounded-full border-2 transition-all duration-200 absolute transform -translate-x-1/2 -translate-y-1/2",
                        expandedCards.has(turn.request?.request_id || "") 
                          ? "w-5 h-5 shadow-lg" 
                          : "w-4 h-4",
                        "border-background",
                        faultCount > 0 
                          ? "bg-red-500" 
                          : turn.response?.correction_applied 
                          ? "bg-green-500" 
                          : "bg-maitai-green"
                      )}>
                      </div>
                    </div>
                    
                          {/* Timeline line - positioned relative to the card container */}
                          {groupIndex < filteredGroupedTimeline.length - 1 && (
                            <div 
                              className="absolute left-1.5 w-px bg-border transition-all duration-200"
                              style={{ 
                                top: "65px",
                                height: "calc(100% - 85px)" // Extends from below dot with more spacing
                              }}
                            ></div>
                          )}

                    {/* Date outside the card */}
                    <div className="text-sm font-medium text-foreground mb-3">
                      {moment(group.turns[0]?.request?.task_start_time || group.turns[0]?.request?.date_created).format("MMM D, YYYY h:mm:ss.SSS A")}
                    </div>

                    <Card 
                      ref={(el) => {
                        if (el && turn.request?.request_id) {
                          cardRefs.current.set(turn.request.request_id, el);
                        }
                      }}
                      className={cn(
                        "w-full cursor-pointer transition-all hover:shadow-md",
                        turn.request?.request_id === selectedRequestId 
                          ? "border-[1.5px] border-maitai-green shadow-lg" 
                          : "border border-border"
                      )}
                      onClick={(e) => {
                        // Don't select if clicking on a button or interactive element
                        const target = e.target as HTMLElement;
                        if (target.closest("button") || target.closest("a") || target.closest("[role=\"button\"]")) {
                          return;
                        }
                        if (turn.request?.request_id) {
                          // Set manual selection flag
                          isManualSelectionRef.current = true;
                          clearTimeout(manualSelectionTimeoutRef.current);
                          
                          setSelectedRequestId(turn.request.request_id);
                          
                          // Clear manual selection flag after a delay
                          manualSelectionTimeoutRef.current = setTimeout(() => {
                            isManualSelectionRef.current = false;
                          }, 500); // 500ms delay before re-enabling scroll selection
                        }
                      }}
                    >
                      <Collapsible
                        open={expandedCards.has(turn.request?.request_id || "")}
                        onOpenChange={(isOpen) => {
                          const requestId = turn.request?.request_id;
                          if (!requestId) return;
                          
                          setExpandedCards(prev => {
                            const newSet = new Set(prev);
                            if (isOpen) {
                              newSet.add(requestId);
                            } else {
                              newSet.delete(requestId);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <CardContent className="p-5">
                          <div className="space-y-4">
                            {/* Timeline header */}
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                {turn.response?.correction_applied ? (
                                  <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 text-xs">
                                    Corrected
                                  </Badge>
                                ) : faultCount > 0 ? (
                                  <Badge variant="destructive" className="text-xs">
                                    {faultCount > 1 ? "FAULTS" : "FAULT"}
                                  </Badge>
                                ) : (
                                  <div className="h-5" /> 
                                )}
                                
                                {/* Actual agent_request status pill - inline with other badges */}
                                {(() => {
                                  // Look for the latest non-empty status in the group
                                  let status = turn.request?.agent_request_status;
                                  
                                  if (!status) {
                                    // Search from latest to earliest for a non-empty status
                                    for (let i = group.turns.length - 1; i >= 0; i--) {
                                      if (group.turns[i].request?.agent_request_status) {
                                        status = group.turns[i].request.agent_request_status;
                                        break;
                                      }
                                    }
                                  }
                                  
                                  if (!status) return null;
                                  
                                  const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
                                  const statusStr = String(status || "").toLowerCase();
                                  let classes = "";
                                  
                                  switch (statusStr) {
                                    case "completed":
                                    case "success":
                                      classes = `${baseClasses} border-green-500 text-green-700 dark:text-white bg-card [background-image:linear-gradient(rgba(34,197,94,0.1),rgba(34,197,94,0.1))] dark:[background-image:linear-gradient(rgba(34,197,94,0.2),rgba(34,197,94,0.2))]`;
                                      break;
                                    case "failed":
                                    case "error":
                                      classes = `${baseClasses} border-red-500 text-red-700 dark:text-white bg-card [background-image:linear-gradient(rgba(239,68,68,0.1),rgba(239,68,68,0.1))] dark:[background-image:linear-gradient(rgba(239,68,68,0.2),rgba(239,68,68,0.2))]`;
                                      break;
                                    case "active":
                                    case "running":
                                    case "in_progress":
                                      classes = `${baseClasses} border-blue-500 text-blue-700 dark:text-white bg-card [background-image:linear-gradient(rgba(59,130,246,0.1),rgba(59,130,246,0.1))] dark:[background-image:linear-gradient(rgba(59,130,246,0.2),rgba(59,130,246,0.2))]`;
                                      break;
                                    case "partial_failure":
                                      classes = `${baseClasses} border-orange-500 text-orange-700 dark:text-white bg-card [background-image:linear-gradient(rgba(251,146,60,0.1),rgba(251,146,60,0.1))] dark:[background-image:linear-gradient(rgba(251,146,60,0.2),rgba(251,146,60,0.2))]`;
                                      break;
                                    default:
                                      classes = `${baseClasses} border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-card`;
                                  }
                                  
                                  return (
                                    <span className={classes}>
                                      {String(status || "UNKNOWN").toUpperCase()}
                                    </span>
                                  );
                                })()}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Tooltip title="View Task Details">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewTaskClick(group.taskId);
                                    }}
                                  >
                                    <ExternalLinkIcon className="w-3 h-3" />
                                  </Button>
                                </Tooltip>
                                
                                <Tooltip title="Copy Request ID">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyRequestId(turn.request?.request_id || "");
                                    }}
                                  >
                                    <CopyIcon className="w-3 h-3" />
                                  </Button>
                                </Tooltip>
                                
                                {turn.request && applicationId && (
                                  <TestSetContextMenu
                                    applicationId={applicationId.toString()}
                                    applicationActionType={turn.request.action_type || ""}
                                    requestId={turn.request.request_id || ""}
                                    testSets={
                                      turn.request.action_type
                                        ? [...(testSetsByAction[turn.request.action_type] || [])].sort((a, b) => b.id - a.id)
                                        : []
                                    }
                                    existingTestSetIds={getExistingTestSetIds(turn.request.metadata)}
                                    onUpdate={handleTestSetUpdate}
                                  >
                                    <Tooltip title="Add to Test Set">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                      >
                                        <PlusIcon className="w-3 h-3" />
                                      </Button>
                                    </Tooltip>
                                  </TestSetContextMenu>
                                )}
                                
                                <Tooltip title="Toggle Agent Timeline">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setExpandedTimelines(prev => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(group.taskId)) {
                                          newSet.delete(group.taskId);
                                        } else {
                                          newSet.add(group.taskId);
                                        }
                                        return newSet;
                                      });
                                    }}
                                  >
                                    <BarChart3Icon className={cn(
                                      "w-4 h-4 transition-colors",
                                      expandedTimelines.has(group.taskId) ? "text-maitai-green" : "text-muted-foreground"
                                    )} />
                                  </Button>
                                </Tooltip>
                                
                                <CollapsibleTrigger className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 p-0">
                                  <></>
                                </CollapsibleTrigger>
                              </div>
                            </div>

                            {/* Task-specific Agent Timeline */}
                            {expandedTimelines.has(group.taskId) && (
                              <div className="mb-4">
                                <AgentTimelineCard
                                  contexts={contexts.filter(ctx => 
                                    group.allTurns.some(turn => turn.request?.request_id === ctx.request_id)
                                  )}
                                  turns={group.allTurns}
                                />
                              </div>
                            )}

                            {/* Response content - now full width */}
                            <div className="space-y-3">
                              {metadata?.summary && (
                                <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                  <div className="flex items-start gap-2">
                                    <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-blue-900 dark:text-blue-100">{metadata.summary}</p>
                                  </div>
                                </div>
                              )}

                              <div className="space-y-2 min-w-0">
                                {group.isRunning ? (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                      <span className="text-muted-foreground italic text-sm">Task running...</span>
                                    </div>
                                  </div>
                                ) : (turn.response as any)?.response_body ? (
                                  /* Display response_body field (works for both LLM and API) */
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden">
                                      {formatResponseContent(String((turn.response as any).response_body))}
                                    </div>
                                  </div>
                                ) : turn.response?.response ? (
                                  /* Fallback to response field for LLM calls */
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words overflow-hidden">
                                      {formatResponseContent(String(turn.response.response))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <span className="text-muted-foreground italic text-sm">
                                      {(turn.response as any)?.feedback || "No response content"}
                                    </span>
                                  </div>
                                )}
                                {turn.request?.error && (
                                  <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-red-900 dark:text-red-100 text-sm break-words">
                                    {turn.request?.error}
                                  </div>
                                )}
                                {(turn.response as any)?.error_message && (
                                  <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg text-red-900 dark:text-red-100 text-sm break-words">
                                    {String((turn.response as any).error_message)}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Metadata row */}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border/50">
                              {/* Show execution time with TTFT for streaming responses */}
                              {(() => {
                                const executionTime = group.turns[0]?.request?.execution_time_ms;
                                const firstTokenTime = (turn.response as any)?.first_token_time;
                                
                                // Check if this is a streaming response (first_token_time > 0)
                                const isStreaming = firstTokenTime != null && Number(firstTokenTime) > 0;
                                
                                if (executionTime != null) {
                                  // Show "ttft/total ms" for streaming, just "total ms" otherwise
                                  if (isStreaming) {
                                    return (
                                      <span className="flex items-center gap-1">
                                        <ClockIcon className="w-3 h-3" />
                                        {firstTokenTime}/{executionTime}ms
                                      </span>
                                    );
                                  }
                                  
                                  return (
                                    <span className="flex items-center gap-1">
                                      <ClockIcon className="w-3 h-3" />
                                      {String(executionTime)}ms
                                    </span>
                                  );
                                }
                                
                                // Fallback to response_time if execution_time not available
                                const responseTime = turn.response?.response_time ?? (turn.response as any)?.response_time_ms;
                                
                                if (responseTime != null) {
                                  if (isStreaming) {
                                    return (
                                      <span className="flex items-center gap-1">
                                        <ClockIcon className="w-3 h-3" />
                                        {firstTokenTime}/{responseTime}ms
                                      </span>
                                    );
                                  }
                                  
                                  return (
                                    <span className="flex items-center gap-1">
                                      <ClockIcon className="w-3 h-3" />
                                      {responseTime}ms
                                    </span>
                                  );
                                }
                                
                                return null;
                              })()}
                              
                              {/* LLM token usage */}
                              {(turn.request as any)?.agent_action_type === "LLM_CALL" && 
                               turn.response?.prompt_tokens != null && 
                               turn.response?.completion_tokens != null && (
                                <span className="flex items-center gap-1">
                                  <ServerIcon className="w-3 h-3" />
                                  {String(turn.response.prompt_tokens)} → {String(turn.response.completion_tokens)}
                                </span>
                              )}
                              
                              {/* API status code */}
                              {(turn.request as any)?.agent_action_type === "API_CALL" && (turn.response as any)?.status_code && (
                                <span className={cn(
                                  "flex items-center gap-1 font-mono",
                                  Number((turn.response as any).status_code) >= 200 && Number((turn.response as any).status_code) < 300
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400"
                                )}>
                                  HTTP {(turn.response as any).status_code}
                                </span>
                              )}
                              
                              {parseInt((turn?.request?.metadata?.reruns as string) || "0") > 0 && (
                                <span>{String(turn.request.metadata.reruns)} rerun{parseInt(turn.request.metadata.reruns as string) > 1 ? "s" : ""}</span>
                              )}
                            </div>

                            {/* Expandable technical details */}
                            <CollapsibleContent>
                              <div className="pt-4 border-t border-border">
                                <h4 className="text-sm font-gc-med text-foreground mb-3">Technical Details</h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Inference</p>
                                    <p className="text-foreground">
                                      {turn.request?.inference_location == "SERVER" ? "Server" : "Client"}
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Model</p>
                                    <p className="text-foreground">
                                      {turn.request?.params?.model || "-"}
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Temperature</p>
                                    <p className="text-foreground">
                                      {turn.request?.params?.temperature !== undefined && turn.request?.params?.temperature !== null 
                                        ? turn.request.params.temperature 
                                        : "-"}
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Streaming</p>
                                    <p className="text-foreground">
                                      {turn.request?.params?.stream ? "On" : "Off"}
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Evaluations</p>
                                    <p className="text-foreground">
                                      {turn.eval_request?.id ? "On" : "Off"}
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <p className="text-muted-foreground font-gc-reg">Corrections</p>
                                    <p className="text-foreground">
                                      {turn.request?.apply_corrections ? "On" : "Off"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </div>
                        </CardContent>
                      </Collapsible>
                    </Card>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Listening indicator */}
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-maitai-green rounded-full animate-pulse"></div>
                Listening for new requests...
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Contexts */}
      <div className="w-[400px] overflow-auto bg-muted/20">
        <div className="p-4">
          <AgentContext
            contexts={contexts}
            selectedRequestId={selectedRequestId}
            showHeader={true}
            isLoading={loadingContexts}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        // Clean up manual selection timeout on close
        if (manualSelectionTimeoutRef.current) {
          clearTimeout(manualSelectionTimeoutRef.current);
        }
        onClose();
      }
    }}>
      <DialogContent className="max-w-[95vw] w-full h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-border flex-shrink-0">
          <DialogTitle className="text-lg font-gc-med">
            Session Details
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentSessionOverview;
